import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushNotifications, PermissionStatus, Token } from '@capacitor/push-notifications';
import { UserService } from './user.service';
import { ENV_CONFIG } from '../../bin/env.config';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { catchError, map, shareReplay, switchMap, finalize } from 'rxjs/operators';
import { SalonmonsterHttpClient } from './salonmonster-http-client';
import { ErrorHandlerService } from './error-handler.service';
import { Utilities } from '../utilities/utilities';
import { NotificationDebugService } from './notification-debug.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends SalonmonsterHttpClient {
  private isInitialized = false;
  private registrationInProgress = false;
  private currentRegistration$: Observable<any> | null = null;

  constructor(
    private platform: Platform,
    protected http: HttpClient,
    protected userService: UserService,
    protected errorHandlerService: ErrorHandlerService,
    private notificationDebugService: NotificationDebugService
  ) {
    super(http, userService, errorHandlerService);
  }

  /**
   * Initialize notification system - request permissions and set up listeners
   * Should be called on app launch
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!Utilities.isCapacitorNative()) {
      return;
    }

    this.isInitialized = true;

    // Request notification permissions
    try {
      const permissionResult = await PushNotifications.requestPermissions();
      this.notificationDebugService.logPermissionStatus(permissionResult);

      if (permissionResult.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();
      }
    } catch (error) {
      console.error('[NOTIFICATION] Error requesting permissions:', error);
      this.notificationDebugService.logError('requestPermissions', error);
    }

    // Set up listeners
    this.setupListeners();

    // Get FCM token and register if user is logged in
    this.getFCMTokenAndRegister();
  }

  /**
   * Register device with backend and subscribe to topic
   * Should be called after user logs in or when app starts with existing session
   */
  async registerDevice(): Promise<Observable<any>> {
    // Prevent concurrent registrations - return shared observable if already in progress
    if (this.registrationInProgress && this.currentRegistration$) {
      // Return the existing registration observable to prevent duplicate HTTP requests
      return this.currentRegistration$;
    }

    if (!Utilities.isCapacitorNative()) {
      return of({ success: false, message: 'Not running on native platform' });
    }

    if (!this.userService.isLoggedIn()) {
      return of({ success: false, message: 'User not logged in' });
    }

    // Set flag immediately to prevent concurrent calls
    this.registrationInProgress = true;

    try {
      // Get FCM token
      const tokenResult = await FirebaseMessaging.getToken();
      const deviceToken = tokenResult.token;

      const user = this.userService.getUser();
      if (!user) {
        throw new Error('User not available');
      }

      const userID = user.getID();

      // Register with backend
      const url = `${ENV_CONFIG.API_ROOT}/stylists/notifications/register`;
      const requestBody = {
        userID: userID,
        deviceToken: deviceToken
      };

      const registrationObservable = this.post(url, requestBody).pipe(
        map((response: any) => {
          this.notificationDebugService.logRegistration(deviceToken, true);
          return { success: true, response: response?.data || response };
        }),
        catchError((error) => {
          console.error('[NOTIFICATION] Registration error:', error);
          this.notificationDebugService.logError('registerDevice', error);
          return of({ success: false, error });
        }),
        // Subscribe to topic after successful registration, then return the registration result
        switchMap((result) => {
          if (result.success) {
            // Chain topic subscription, but always return the registration result
            return this.subscribeToTopic(userID.toString()).pipe(
              map(() => result), // Return the original registration result
              catchError(() => of(result)) // If topic subscription fails, still return registration result
            );
          }
          return of(result);
        }),
        shareReplay(1), // Multicast the observable to prevent duplicate HTTP requests
        // Reset flags after completion (success or error) - finalize runs regardless of success/error
        finalize(() => {
          this.registrationInProgress = false;
          this.currentRegistration$ = null;
        })
      );

      // Store the observable so concurrent calls can share it
      this.currentRegistration$ = registrationObservable;

      return registrationObservable;
    } catch (error) {
      console.error('[NOTIFICATION] Exception in registerDevice:', error);
      this.notificationDebugService.logError('registerDevice', error);
      // Reset flags if error occurs before observable is created
      this.registrationInProgress = false;
      this.currentRegistration$ = null;
      return of({ success: false, error });
    }
  }

  /**
   * Subscribe to a topic (e.g., stylistID)
   */
  subscribeToTopic(topic: string): Observable<any> {
    if (!Utilities.isCapacitorNative()) {
      return of({ success: false, message: 'Not running on native platform' });
    }

    if (!topic || typeof topic !== 'string' || topic.trim() === '') {
      console.error('[NOTIFICATION] Invalid topic:', topic);
      return of({ success: false, message: 'Invalid topic' });
    }

    return from(FirebaseMessaging.subscribeToTopic({ topic: topic.trim() })).pipe(
      map(() => {
        this.notificationDebugService.logTopicSubscription(topic, true);
        return { success: true, topic };
      }),
      catchError((error) => {
        console.error('[NOTIFICATION] Error subscribing to topic:', error);
        this.notificationDebugService.logTopicSubscription(topic, false, error);
        return of({ success: false, error, topic });
      })
    );
  }

  /**
   * Get FCM token and register device if user is logged in
   */
  private async getFCMTokenAndRegister(): Promise<void> {
    if (!Utilities.isCapacitorNative()) {
      return;
    }

    try {
      const tokenResult = await FirebaseMessaging.getToken();
      const deviceToken = tokenResult.token;
      // Log token retrieval (not registration yet - that happens in registerDevice)
      this.notificationDebugService.logRegistration(deviceToken, false);

      // If user is logged in, register device
      if (this.userService.isLoggedIn()) {
        (await this.registerDevice()).subscribe();
      }
    } catch (error) {
      console.error('[NOTIFICATION] Error getting FCM token:', error);
      this.notificationDebugService.logRegistration(null, false, error);
    }
  }

  /**
   * Set up notification listeners for foreground, background, and killed states
   */
  private setupListeners(): void {
    if (!Utilities.isCapacitorNative()) {
      return;
    }

    // Listen for registration token (Capacitor PushNotifications)
    PushNotifications.addListener('registration', async (token: Token) => {
      // Log token retrieval (not registration yet - that happens in registerDevice)
      this.notificationDebugService.logRegistration(token.value, false);

      // If user is logged in, register with backend
      if (this.userService.isLoggedIn()) {
        (await this.registerDevice()).subscribe();
      }
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('[NOTIFICATION] Push registration error:', error);
      this.notificationDebugService.logRegistration(null, false, error);
    });

    // Listen for notifications received (foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
      this.notificationDebugService.logNotificationReceived(notification);
    });

    // Listen for notification actions (when user taps notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
      this.notificationDebugService.logNotificationReceived(action.notification);
    });

    // Listen for FCM notifications (Firebase Messaging)
    FirebaseMessaging.addListener('notificationReceived', (event: any) => {
      this.notificationDebugService.logNotificationReceived(event);
    }).catch((error) => {
      console.error('[NOTIFICATION] Error adding FCM notification listener:', error);
      this.notificationDebugService.logError('addNotificationListener', error);
    });

    FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
      this.notificationDebugService.logNotificationReceived(event);
    }).catch((error) => {
      console.error('[NOTIFICATION] Error adding FCM action listener:', error);
      this.notificationDebugService.logError('addActionListener', error);
    });
  }

  /**
   * Check notification permissions
   */
  async checkPermissions(): Promise<PermissionStatus> {
    if (!Utilities.isCapacitorNative()) {
      return { receive: 'prompt' };
    }

    try {
      const status = await PushNotifications.checkPermissions();
      this.notificationDebugService.logPermissionStatus(status);
      return status;
    } catch (error) {
      console.error('[NOTIFICATION] Error checking permissions:', error);
      this.notificationDebugService.logError('checkPermissions', error);
      return { receive: 'prompt' };
    }
  }

  /**
   * Request notification permissions
   * On iOS, if permissions were previously denied, this will not show a prompt.
   * Returns the permission status after the request.
   */
  async requestPermissions(): Promise<PermissionStatus> {
    if (!Utilities.isCapacitorNative()) {
      return { receive: 'prompt' };
    }

    try {
      // First check current status
      const currentStatus = await PushNotifications.checkPermissions();
      
      // If already denied, we can't request again on iOS
      if (currentStatus.receive === 'denied') {
        return currentStatus;
      }

      // Request permissions (will show prompt if not determined, or return current status if already granted)
      const permissionResult = await PushNotifications.requestPermissions();
      this.notificationDebugService.logPermissionStatus(permissionResult);

      // If permission was granted, register for push notifications
      if (permissionResult.receive === 'granted') {
        await PushNotifications.register();
        
        // If user is logged in, register device with backend
        if (this.userService.isLoggedIn()) {
          (await this.registerDevice()).subscribe();
        }
      }

      return permissionResult;
    } catch (error) {
      console.error('[NOTIFICATION] Error requesting permissions:', error);
      this.notificationDebugService.logError('requestPermissions', error);
      return { receive: 'prompt' };
    }
  }

  /**
   * Reset initialization flag (useful for testing)
   */
  reset(): void {
    this.isInitialized = false;
    this.registrationInProgress = false;
    this.currentRegistration$ = null;
  }
}

